import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  AddressField,
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationQuestionGroup,
  CommonQuestionKeys,
  Expression,
  FieldType,
  PaymentMethod,
  SpecialFieldKeys,
  TextInputType,
} from '@bybeam/platform-types';
import { SpecialQuestionKeys } from '@platform-ui/utilities/application/configs/standards/types.js';
import { answer, eq, not } from '../../ast/utils.js';
import STANDARD_COMPONENTS from '../standards/index.js';

enum ApplicantType {
  Tenant = 'TENANT',
  Landlord = 'LANDLORD',
}

function isApplicantType(type: ApplicantType): Expression {
  return eq(SpecialFieldKeys.ApplicantType, type);
}

const APPLICANT_TYPE_GROUP: ApplicationQuestionGroup = {
  key: SpecialFieldKeys.ApplicantType,
  name: 'Applicant Type',
  questions: [
    {
      key: SpecialFieldKeys.ApplicantType,
      copy: { title: 'What type of applicant are you?' },
      fields: [
        {
          key: SpecialFieldKeys.ApplicantType,
          type: FieldType.RadioList,
          options: [
            { label: 'Tenant', value: ApplicantType.Tenant },
            { label: 'Landlord', value: ApplicantType.Landlord },
          ],
        },
      ],
    },
  ],
};

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'What are Housing Vouchers?',
      body: `
Section 8, also known as the Housing Choice Voucher (HCV) program, provides federal 
funding for subsidies that help eligible low-income families rent decent, safe, and 
affordable housing in a neighborhood of their choice. **These funds do not require repayment.**

You can find a summary of the documents required and other info for 
[tenant subsidy applications for HPD’s various rental subsidy programs here](#).`,
    },
    STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
  ],
  sections: [
    {
      key: 'personalInfo',
      name: 'Personal Info',
      overview: {
        title: 'Personal Info',
        description: 'In this section, you’ll provide us with information about you.',
      },
      questionGroups: [
        APPLICANT_TYPE_GROUP,
        {
          key: 'personalInfo',
          name: 'Personal Info',
          questions: [
            {
              ...STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.MAILING_ADDRESS],
              dynamicLogic: isApplicantType(ApplicantType.Landlord),
            },
            {
              ...STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.GENDER],
              dynamicLogic: isApplicantType(ApplicantType.Tenant),
            },
            {
              ...STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.RACE],
              dynamicLogic: isApplicantType(ApplicantType.Tenant),
            },
          ],
        },
      ],
    },
    {
      key: 'eligiblityInfo',
      name: 'Eligibility Information',
      overview: {
        title: 'Your eligibility',
        description:
          'In this section, you will provide us with more information about your application.',
      },
      questionGroups: [
        mergeObjects(
          [
            APPLICANT_TYPE_GROUP,
            {
              key: `eligibilityInfo.${SpecialFieldKeys.ApplicantType}`,
              dynamicLogic: not(answer(SpecialFieldKeys.ApplicantType)),
              questions: [{ key: `eligibilityInfo.${SpecialFieldKeys.ApplicantType}` }],
            } as ApplicationQuestionGroup,
          ],
          'merge',
        ),
        {
          key: 'addressAndLandlord',
          name: 'Address & Landlord',
          dynamicLogic: isApplicantType(ApplicantType.Tenant),
          overview: {
            title: 'Now, we will gather your address and landlord information.',
            description:
              'In this section, you’ll enter your address, and your landlord’s contact information.',
          },
          questions: [
            mergeObjects(
              [
                STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.ADDRESS],
                {
                  copy: {
                    intro:
                      'Please complete the relevant fields of your home address. You must enter an address in New York state to qualify.',
                  },
                  fields: [{ fiter: { state: 'NY' } } as Partial<AddressField>],
                } as ApplicationQuestion,
              ],
              'merge',
            ),
            {
              key: 'address.landlord',
              copy: {
                title: 'What’s your landlord’s contact information?',
                intro:
                  'Provide all information you have on your landlord and {PARTNER_NAME} will contact your landlord.',
              },
              fields: [
                {
                  type: FieldType.Typography,
                  key: 'address.landlord.contactHeader',
                  copy: 'Landlord Contact Information',
                  props: { variant: 'h3' },
                },
                {
                  type: FieldType.Text,
                  inputType: TextInputType.Text,
                  key: 'address.landlord.contactName',
                  copy: 'Primary Contact Name',
                  props: {
                    tooltip:
                      'Please enter the full name of the person you correspond with about the place you rent. This typically is a landlord or a property manager.',
                  },
                },
                {
                  type: FieldType.Text,
                  inputType: TextInputType.Text,
                  key: 'address.landlord.companyName',
                  copy: 'Landlord Company Name (Optional)',
                  props: {
                    tooltip:
                      'This is the name of the real estate company that owns your building, if applicable.',
                  },
                  validation: { required: false },
                },
                {
                  type: FieldType.Text,
                  inputType: TextInputType.Phone,
                  key: 'address.landlord.phone',
                  copy: 'Primary Contact Phone Number',
                  validation: { required: false },
                },
                {
                  type: FieldType.Text,
                  inputType: TextInputType.Email,
                  key: 'address.landlord.email',
                  copy: 'Primary Contact Email Address',
                  validation: { required: false },
                },
                {
                  type: FieldType.Typography,
                  key: 'address.landlord.addressHeader.tenant',
                  copy: 'Landlord Mailing Address',
                  props: { variant: 'h3' },
                },
                {
                  type: FieldType.Address,
                  key: 'address.landlordMailingAddress',
                  copy: 'Landlord Mailing Address',
                  validation: { required: false },
                },
              ],
            },
          ],
        },
        {
          key: 'householdInfo',
          name: 'Household Information',
          dynamicLogic: isApplicantType(ApplicantType.Tenant),
          overview: {
            title: 'Now, let’s gather information about your household',
            description:
              'In this part of the application, you’ll share information about your household size and members.',
          },
          questions: [
            STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.HOUSEHOLD_MEMBERS],
          ],
        },
        {
          key: SpecialQuestionKeys.W9,
          name: 'W9 Information',
          dynamicLogic: isApplicantType(ApplicantType.Landlord),
          questions: [
            {
              key: SpecialQuestionKeys.W9,
              copy: { title: 'W9 Information' },
              fields: [],
            },
          ],
        },
        {
          key: 'paymentInfo',
          name: 'Payment Information',
          dynamicLogic: isApplicantType(ApplicantType.Landlord),
          questions: [
            {
              key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
              copy: { title: 'How would you like to receive your payment?' },
              fields: [
                {
                  key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
                  type: FieldType.RadioList,
                  options: [
                    {
                      label: 'Direct Deposit',
                      description:
                        'We recommend using this option if you have access to a bank account. You should receive your funds in 1-3 business days.',
                      value: PaymentMethod.DirectDeposit,
                    },
                    {
                      label: 'Paper Check',
                      description: 'Receive a check in the mail in 7-10 business days.',
                      value: PaymentMethod.Check,
                    },
                  ],
                },
              ],
            },
            {
              key: SpecialQuestionKeys.BankAccount,
              copy: { title: 'BANK ACCOUNT INFORMATION' },
              fields: [],
              dynamicLogic: eq('preferredPaymentMethod', PaymentMethod.DirectDeposit),
            },
          ],
        },
      ],
    },
    {
      key: 'documents',
      name: 'Document Upload',
      overview: {
        title: 'Your document upload',
        description:
          'Your documents will be carefully reviewed. Upload the required documents which will be used to determine eligibility.',
      },
      questionGroups: [
        mergeObjects(
          [
            APPLICANT_TYPE_GROUP,
            {
              key: `documents.${SpecialFieldKeys.ApplicantType}`,
              dynamicLogic: not(answer(SpecialFieldKeys.ApplicantType)),
              questions: [{ key: `documents.${SpecialFieldKeys.ApplicantType}` }],
            } as ApplicationQuestionGroup,
          ],
          'merge',
        ),
        {
          key: 'documents',
          name: 'Documents Uploaded',
          questions: [
            {
              key: 'documents.tenant',
              copy: {
                title: 'Document Upload',
                intro: 'Please upload documents to help us verify your information. ',
              },
              layout: 'panel',
              dynamicLogic: isApplicantType(ApplicantType.Tenant),
              fields: [
                {
                  type: FieldType.Document,
                  key: 'documents.tenant',
                  copy: {
                    title: 'Supplemental Documents',
                    description: `Examples include **Proof of Chronic Homeslessness, Proof of Homelessness, 
Proof of Qualifying Disability, Executed Lease, Application for Rental Assistance, Proof of Income, 
Proof of Assets, Request for Tenancy Approval,  Proof of SSN, Government‐Issued Photo ID**
or other documentation that may support your case.`,
                  },
                },
              ],
            },
            {
              key: 'documents.landlord',
              copy: {
                title: 'Document Upload',
                intro: 'Please upload documents to help us verify your information. ',
              },
              layout: 'panel',
              dynamicLogic: isApplicantType(ApplicantType.Landlord),
              fields: [
                {
                  type: FieldType.Document,
                  key: 'documents.landlord',
                  copy: {
                    title: 'Required Documents',
                    description:
                      'Please include the following documents: **Landlord Package, Rent Roll** and **TCO**.',
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    STANDARD_COMPONENTS.definitions.sections.review,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: 'Apply for Housing Vouchers',
        message: `
You could be found eligible to receive assistance up to $**10,000**. These funds do not 
require repayment and are intended to help people who are in high need of financial assistance.`,
      },
    ],
  },
};
export default CONFIG;
